import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  Chip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
} from '@mui/icons-material';
import { usersAPI, apiUtils } from '../services/api';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    mobile: '',
    role: 'Staff',
    active: 1,
    password: '',
  });
  const [error, setError] = useState('');

  // Load users from API
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await usersAPI.list();
      setUsers(apiUtils.formatResponse(response) || []);
    } catch (error) {
      setError(apiUtils.handleError(error));
      // Fallback to mock data if API fails
      setUsers([
        {
          id: 1,
          username: 'admin',
          email: 'admin@tiendaone.com',
          firstName: 'Admin',
          lastName: 'User',
          role: 'admin',
          status: 'active',
          lastLogin: '2024-01-20 10:30:00',
          createdAt: '2024-01-01',
        },
        {
          id: 2,
          username: 'cashier1',
          email: 'cashier1@tiendaone.com',
          firstName: 'Maria',
          lastName: 'Garcia',
          role: 'cashier',
          status: 'active',
          lastLogin: '2024-01-19 14:15:00',
          createdAt: '2024-01-05',
        },
        {
          id: 3,
          username: 'manager1',
          email: 'manager1@tiendaone.com',
          firstName: 'John',
          lastName: 'Smith',
          role: 'manager',
          status: 'inactive',
          lastLogin: '2024-01-18 09:00:00',
          createdAt: '2024-01-03',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { value: 'Administrator', label: 'Administrator' },
    { value: 'Manager', label: 'Manager' },
    { value: 'Staff', label: 'Staff' },
    { value: 'Viewer', label: 'Viewer' },
  ];

    const columns = [
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            color="primary"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleToggleStatus(params.row)}
            color={params.row.active === 1 ? 'warning' : 'success'}
          >
            {params.row.active === 1 ? <LockIcon /> : <LockOpenIcon />}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.id)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'username', headerName: 'Username', width: 150 },
    { field: 'email', headerName: 'Email', width: 200 },
    { 
      field: 'full_name', 
      headerName: 'Full Name', 
      width: 200,
      valueGetter: (value, row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'N/A'
    },
    { field: 'mobile', headerName: 'Mobile', width: 130 },
    { field: 'role', headerName: 'Role', width: 100 },
    {
      field: 'active',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value === 1 ? 'Active' : 'Inactive'}
          color={params.value === 1 ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    { field: 'date_created', headerName: 'Created', width: 120 },
  ];

  const handleAdd = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      mobile: '',
      role: 'Staff',
      active: 1,
      password: '',
    });
    setDialogOpen(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username || '',
      email: user.email || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      mobile: user.mobile || '',
      role: user.role || 'Staff',
      active: user.active || 1,
      password: '',
    });
    setDialogOpen(true);
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.active === 1 ? 0 : 1;
    try {
      await usersAPI.updateNoPassword(user.id, { ...user, active: newStatus });
      setUsers(users.map(u => 
        u.id === user.id 
          ? { ...u, active: newStatus }
          : u
      ));
    } catch (error) {
      setError(apiUtils.handleError(error));
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await usersAPI.delete(userId);
        setUsers(users.filter(u => u.id !== userId));
      } catch (error) {
        setError(apiUtils.handleError(error));
      }
    }
  };

  const handleSave = async () => {
    if (!formData.username || !formData.email || !formData.first_name || !formData.last_name) {
      setError('Please fill in all required fields');
      return;
    }

    if (!editingUser && !formData.password) {
      setError('Password is required for new users');
      return;
    }

    const userData = {
      username: formData.username,
      email: formData.email,
      first_name: formData.first_name,
      last_name: formData.last_name,
      mobile: formData.mobile,
      role: formData.role,
      active: parseInt(formData.active),
    };

    // Only include password if provided
    if (formData.password) {
      userData.password = formData.password;
    }

    try {
      if (editingUser) {
        // Update existing user
        if (formData.password) {
          // Update with password
          await usersAPI.update(editingUser.id, userData);
        } else {
          // Update without password
          const { password, ...userDataNoPassword } = userData;
          await usersAPI.updateNoPassword(editingUser.id, userDataNoPassword);
        }
        setUsers(users.map(u => 
          u.id === editingUser.id 
            ? { ...u, ...userData }
            : u
        ));
      } else {
        // Add new user
        const response = await usersAPI.create(userData);
        const newUser = apiUtils.formatResponse(response);
        setUsers([...users, newUser]);
      }

      setDialogOpen(false);
      setError('');
    } catch (error) {
      setError(apiUtils.handleError(error));
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 3, flexShrink: 0 }}>
        <Typography variant="h4" component="h1">
          Users Management
        </Typography>
      </Box>

      <Paper 
        sx={{ 
          height: 'calc(100vh - 140px)', 
          width: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <DataGrid
          rows={users}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          loading={loading}
          disableSelectionOnClick
          autoHeight={false}
          disableVirtualization={false}
          sx={{
            height: '100%',
            width: '100%',
            border: 'none',
            '& .MuiDataGrid-main': {
              overflow: 'auto',
            },
            '& .MuiDataGrid-virtualScroller': {
              overflow: 'auto !important',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid',
              borderColor: 'divider',
              minHeight: '52px',
              backgroundColor: 'background.paper',
              justifyContent: 'flex-start',
              '& .MuiTablePagination-root': {
                marginLeft: 0,
              },
              '& .MuiTablePagination-toolbar': {
                justifyContent: 'flex-start',
              },
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'background.paper',
              borderBottom: '2px solid',
              borderColor: 'divider',
            },
          }}
        />
      </Paper>

      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent 
          sx={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: '4px',
            },
          }}
        >
          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          {/* Personal Information Section */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>
              Personal Information
            </Typography>
            
            <TextField
              name="first_name"
              label="First Name *"
              value={formData.first_name}
              onChange={handleInputChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            
            <TextField
              name="last_name"
              label="Last Name *"
              value={formData.last_name}
              onChange={handleInputChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            
            <TextField
              name="mobile"
              label="Mobile Number"
              value={formData.mobile}
              onChange={handleInputChange}
              fullWidth
              sx={{ mb: 2 }}
            />
          </Box>

          {/* Account Information Section */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>
              Account Information
            </Typography>
            
            <TextField
              name="username"
              label="Username *"
              value={formData.username}
              onChange={handleInputChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            
            <TextField
              name="email"
              label="Email *"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            
            {!editingUser && (
              <TextField
                name="password"
                label="Password *"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                fullWidth
                required
                sx={{ mb: 2 }}
              />
            )}
          </Box>

          {/* Role & Status Section */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>
              Role & Status
            </Typography>
            
            <TextField
              name="role"
              label="Role *"
              select
              value={formData.role}
              onChange={handleInputChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            >
              <MenuItem value="Administrator">Administrator</MenuItem>
              <MenuItem value="Manager">Manager</MenuItem>
              <MenuItem value="Staff">Staff</MenuItem>
              <MenuItem value="Viewer">Viewer</MenuItem>
            </TextField>
            
            <TextField
              name="active"
              label="Status *"
              select
              value={formData.active}
              onChange={handleInputChange}
              fullWidth
              required
            >
              <MenuItem value={1}>Active</MenuItem>
              <MenuItem value={0}>Inactive</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained">
            {editingUser ? 'Update' : 'Add'} User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for Add User */}
      <Fab
        color="primary"
        aria-label="add user"
        onClick={handleAdd}
        sx={{
          position: 'fixed',
          bottom: 90,
          right: 24,
          zIndex: 1000,
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default Users;
